import React, { useState, useEffect, useCallback } from 'react';
import { api, LoginHistoryEntry } from '../services/api';

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Compute session durations by pairing Login/Logout events per user
function computeSessions(entries: LoginHistoryEntry[]) {
  // Group by actor email, sorted oldest-first
  const byUser: Record<string, LoginHistoryEntry[]> = {};
  [...entries].reverse().forEach(e => {
    if (!byUser[e.ActorEmail]) byUser[e.ActorEmail] = [];
    byUser[e.ActorEmail].push(e);
  });

  const sessions: {
    email: string; name: string;
    loginAt: string; logoutAt: string | null; durationMin: number | null;
  }[] = [];

  Object.entries(byUser).forEach(([email, evts]) => {
    let pendingLogin: LoginHistoryEntry | null = null;
    evts.forEach(e => {
      if (e.Action === 'User.Login') {
        pendingLogin = e;
      } else if (e.Action === 'User.Logout' && pendingLogin) {
        const dur = Math.round(
          (new Date(e.CreatedAt).getTime() - new Date(pendingLogin.CreatedAt).getTime()) / 60000
        );
        sessions.push({
          email, name: e.ActorName,
          loginAt: pendingLogin.CreatedAt, logoutAt: e.CreatedAt, durationMin: dur,
        });
        pendingLogin = null;
      }
    });
    // Unpaired login — still active or no logout recorded
    if (pendingLogin) {
      sessions.push({
        email,
        name:        (pendingLogin as LoginHistoryEntry).ActorName,
        loginAt:     (pendingLogin as LoginHistoryEntry).CreatedAt,
        logoutAt:    null,
        durationMin: null,
      });
    }
  });

  return sessions.sort((a, b) => new Date(b.loginAt).getTime() - new Date(a.loginAt).getTime());
}

function formatDuration(min: number | null) {
  if (min === null) return (
    <span style={{ color: 'var(--success-text, #065f46)', fontSize: 12, fontWeight: 500 }}>Active</span>
  );
  if (min < 1)  return '< 1 min';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const fieldStyle: React.CSSProperties = {
  padding: '7px 10px', fontSize: 13,
  border: '1px solid var(--border2)', borderRadius: 6,
  background: 'var(--surface)', color: 'var(--text1)', fontFamily: 'var(--font)',
};

type ViewMode = 'sessions' | 'raw';

export function LoginHistoryPage() {
  const [entries,  setEntries]  = useState<LoginHistoryEntry[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [search,   setSearch]   = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [view,     setView]     = useState<ViewMode>('sessions');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.admin.loginHistory.list({
      search:   search   || undefined,
      dateFrom: dateFrom || undefined,
      dateTo:   dateTo   || undefined,
    })
      .then(setEntries)
      .catch(err => setError(err?.message ?? 'Failed to load login history'))
      .finally(() => setLoading(false));
  }, [search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  function exportCSV() {
    const headers = view === 'sessions'
      ? ['Name', 'Email', 'Login', 'Logout', 'Duration']
      : ['Date', 'Actor', 'Email', 'Event'];
    const rows = view === 'sessions'
      ? computeSessions(entries).map(s => [
          s.name, s.email,
          formatDateTime(s.loginAt),
          s.logoutAt ? formatDateTime(s.logoutAt) : 'Still active',
          s.durationMin !== null ? String(s.durationMin) + ' min' : 'Active',
        ])
      : entries.map(e => [formatDateTime(e.CreatedAt), e.ActorName, e.ActorEmail, e.Action]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `login-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const sessions = computeSessions(entries);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Login History</h1>
          <p className="page-sub">User login and logout activity across the portal</p>
        </div>
      </div>

      {/* Summary stats — only show when loaded successfully */}
      {!loading && !error && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Logins',    value: entries.filter(e => e.Action === 'User.Login').length },
            { label: 'Total Logouts',   value: entries.filter(e => e.Action === 'User.Logout').length },
            { label: 'Active Sessions', value: sessions.filter(s => s.logoutAt === null).length },
            { label: 'Unique Users',    value: new Set(entries.map(e => e.ActorEmail)).size },
          ].map(item => (
            <div key={item.label} style={{
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '10px 16px', minWidth: 110,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text1)' }}>{item.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{item.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="field-input"
          style={{ width: 240, fontSize: 13 }}
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>From</span>
          <input type="date" style={fieldStyle} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>To</span>
          <input type="date" style={fieldStyle} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        {(search || dateFrom || dateTo) && (
          <button className="btn btn-sm" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}>
            Clear
          </button>
        )}

        {/* View toggle */}
        <div style={{
          display: 'flex', marginLeft: 'auto',
          border: '1px solid var(--border2)', borderRadius: 6, overflow: 'hidden',
        }}>
          {(['sessions', 'raw'] as ViewMode[]).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: '6px 14px', fontSize: 12, border: 'none', cursor: 'pointer',
              background: view === v ? 'var(--accent)' : 'var(--surface)',
              color:      view === v ? '#fff' : 'var(--text2)',
              fontWeight: view === v ? 600 : 400,
              fontFamily: 'var(--font)',
            }}>
              {v === 'sessions' ? 'Sessions' : 'Raw Events'}
            </button>
          ))}
        </div>

        <button className="btn btn-sm" onClick={exportCSV} disabled={entries.length === 0}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>
          Loading login history...
        </div>
      ) : error ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--danger, #dc2626)' }}>
          {error}&nbsp;—&nbsp;
          <button className="btn btn-sm" onClick={load}>Retry</button>
        </div>
      ) : view === 'sessions' ? (
        // ── Sessions view ──────────────────────────────────────────────────────
        sessions.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>No sessions found.</div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Login</th>
                  <th>Logout</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{s.name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{s.email}</div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {formatDateTime(s.loginAt)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {s.logoutAt
                        ? formatDateTime(s.logoutAt)
                        : <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>No logout recorded</span>
                      }
                    </td>
                    <td style={{ fontSize: 13 }}>{formatDuration(s.durationMin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        // ── Raw events view ────────────────────────────────────────────────────
        entries.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>No events found.</div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Date & Time</th>
                  <th>User</th>
                  <th>Event</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.AuditID}>
                    <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.CreatedAt)}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{e.ActorName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{e.ActorEmail}</div>
                    </td>
                    <td>
                      <span className={`badge ${e.Action === 'User.Login' ? 'badge-success' : 'badge-neutral'}`}>
                        {e.Action === 'User.Login' ? '→ Login' : '← Logout'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
